import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Link } from 'react-router-dom';
import ViewDevModal from './ViewDevModal';  // View modal
import EditDevModal from './EditDevModal';  // Edit modal
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import api from '../../services/api';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

const DevsTable = () => {
  const [devs, setDevs] = useState([]);

  // For searching by dev name (or any other fields you choose)
  const [filterText, setFilterText] = useState('');

  // Modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

  const [currentDev, setCurrentDev] = useState(null);
  const [editedDev, setEditedDev] = useState(null);
  const [deletedDev, setDeletedDev] = useState(null);

  // Fetch the list of Devs
  const fetchDevelopers = async () => {
    try {
      const res = await api.get('/developers/unpaginated');
      if (res.data.response) {
        setDevs(res.data.response.data || []);
      }
    } catch (error) {
      console.error('Error fetching developers:', error);
      toast.error('Failed to load developers');
    }
  };

  useEffect(() => {
    fetchDevelopers();
  }, []);

  // View
  const handleView = (dev) => {
    setCurrentDev(dev);
    setShowViewModal(true);
  };
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setCurrentDev(null);
    // Refresh data after modal close
    fetchDevelopers();
  };

  // Edit
  const handleEdit = (dev) => {
    setEditedDev(dev);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditedDev(null);
    // Refresh data after modal close
    fetchDevelopers();
  };

  // Save updated dev in local state and refresh
  const handleSaveEdit = (updatedDev) => {
    const updatedDevs = devs.map((dev) =>
      dev.id === updatedDev.id ? updatedDev : dev
    );
    setDevs(updatedDevs);
    setShowEditModal(false);
    setEditedDev(null);
    fetchDevelopers();
  };

  // Delete
  const handleDeleteDev = (dev) => {
    setDeletedDev(dev);
    setShowDeleteConfirmationModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteConfirmationModal(false);
    setDeletedDev(null);
    // Refresh data
    fetchDevelopers();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/developers/${deletedDev.id}`);
      toast.success('Deletion request is submitted! Wait for approval.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting dev.');
      console.error(error);
    } finally {
      handleCloseDeleteModal();
    }
  };

  // DataTable columns
  const columns = [
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      width:'250px'
    },
     {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
       width: '400px'
    },
    {
      name: 'Projects',
      selector: (row) => row.project_count,
      sortable: true,
      width: '150px',
    },
    {
      name: 'Units',
      selector: (row) => row.unit_count,
      sortable: true,
      width: '150px',
    },
    {
      name: 'Created At',
      selector: (row) => row.created_at
        ? new Date(row.created_at).toLocaleDateString('en-GB')
        : 'N/A',
      sortable: true,
      width: '150px',
    },

    {
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex">
          <button
            onClick={() => handleView(row)}
            className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="iconamoon:eye-light" />
          </button>
          <button
            onClick={() => handleEdit(row)}
            className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="lucide:edit" />
          </button>
          <button
            onClick={() => handleDeleteDev(row)}
            className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // Filtering (by name here, but you can expand)
  const filteredData = devs.filter((dev) => {
    const name = dev.name?.toLowerCase() || '';
    const search = filterText.toLowerCase();
    return name.includes(search);
  });

  // CSV export
  const csvHeaders = [
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Contact Number', key: 'contactNumber' },
    { label: 'Projects', key: 'project_count' },
    { label: 'Units', key: 'unit_count' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Updated At', key: 'updated_at' }
  ];

  // Optional styling for row font size, etc.
  const customStyles = {
    rows: {
      style: {
        fontSize: '15px',
      },
    },
    cells: {
      style: {
        padding: '1px', // Adjust this value to control spacing
      },
    },
  };

  return (
    <div className="card basic-data-table px-3 py-3">


      <div className="card-body">
        {/* Filter and CSV Export */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <input
            type="text"
            placeholder="Search by name"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="form-control"
            style={{ maxWidth: '300px' }}
          />



          <CSVLink
            data={devs} // Export all developers
            headers={csvHeaders}
            filename="developers-data-all.csv"
            className="btn btn-secondary"
          >
            Export All Developers CSV
          </CSVLink>
          {/* "Add New Developer" button */}

          <Link
            to="/add-dev"
            className="btn btn-primary btn-sm d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="icon text-sm" />
            Add New Developer
          </Link>
        </div>

        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          responsive
          striped
          customStyles={customStyles}
          noDataComponent="No results"
        />
      </div>

      {/* View Dev Modal */}
      <ViewDevModal
        show={showViewModal}
        handleClose={handleCloseViewModal}
        dev={currentDev}
      />

      {/* Edit Dev Modal */}
      <EditDevModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        dev={editedDev}
        handleSave={handleSaveEdit}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirmationModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
        content={
          <>
            <Modal.Header>
              <Modal.Title>Delete Dev</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Are you sure you want to delete this developer?</p>
            </Modal.Body>
          </>
        }
      />
    </div>
  );
};

export default DevsTable;
